import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { AlertTriangle, Camera, FileText, PenLine, Trash2, Upload, XCircle } from 'lucide-react'
import { toast } from 'sonner'
import { api } from '../../../lib/api'
import { deschideFisier } from '../../../lib/downloadFile'
import { micsoreazaPoza } from '../../../lib/micsoreazaPoza'
import { AntetFix } from '../../../components/ui/AntetFix'
import { Pastila } from '../../../components/ui/Pastila'
import { useConfirmare } from '../../../components/ui/Confirmare'
import { useAuthStore } from '../../../stores/auth'
import {
  LinkSofer,
  StarePv,
  cand,
  mesajEroare,
  useMasini,
  useOameni,
  type PvDetaliu,
} from './flotaComun'

/** `datetime-local` lucreaza in ora calculatorului, fara fus: conversia in ambele sensuri. */
function laInput(iso: string): string {
  const d = new Date(iso)
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`
}
const dinInput = (v: string) => new Date(v).toISOString()

interface Formular {
  vehicleId: string
  predatorId: string
  primitorId: string
  predatLa: string
  kilometraj: string
  nivelCarburant: string
  stare: string
  daune: string
  actTalon: boolean
  actRca: boolean
  actItp: boolean
  actRovinieta: boolean
  cardCarburant: boolean
  nrChei: string
  alteAccesorii: string
  observatii: string
}

/** Valoarea selectului pentru firma (fara om). */
const FIRMA = 'firma'

function dinPv(p: PvDetaliu): Formular {
  return {
    vehicleId: String(p.vehicleId),
    predatorId: p.predatorId == null ? FIRMA : String(p.predatorId),
    primitorId: p.primitorId == null ? FIRMA : String(p.primitorId),
    predatLa: laInput(p.predatLa),
    kilometraj: String(p.kilometraj),
    nivelCarburant: String(p.nivelCarburant),
    stare: p.stare ?? '',
    daune: p.daune ?? '',
    actTalon: p.actTalon,
    actRca: p.actRca,
    actItp: p.actItp,
    actRovinieta: p.actRovinieta,
    cardCarburant: p.cardCarburant,
    nrChei: String(p.nrChei),
    alteAccesorii: p.alteAccesorii ?? '',
    observatii: p.observatii ?? '',
  }
}

function catreApi(f: Formular) {
  const parte = (v: string) => (v === FIRMA ? null : Number(v))
  return {
    vehicleId: Number(f.vehicleId),
    predatorId: parte(f.predatorId),
    primitorId: parte(f.primitorId),
    predatLa: dinInput(f.predatLa),
    kilometraj: Number(f.kilometraj),
    nivelCarburant: Number(f.nivelCarburant),
    stare: f.stare,
    daune: f.daune,
    actTalon: f.actTalon,
    actRca: f.actRca,
    actItp: f.actItp,
    actRovinieta: f.actRovinieta,
    cardCarburant: f.cardCarburant,
    nrChei: Number(f.nrChei || 0),
    alteAccesorii: f.alteAccesorii,
    observatii: f.observatii,
  }
}

/**
 * Un proces-verbal de predare-primire: completare (ciornă), tipărire, semnare, scanul semnat.
 * `/operational/flota/pv/nou` deschide unul gol; din „nealocat” vine cu mașina și data puse.
 */
export function PvPage() {
  const { id } = useParams<{ id: string }>()
  const nou = id === 'nou'
  const [sp] = useSearchParams()
  const nav = useNavigate()
  const qc = useQueryClient()
  const { confirma, cere } = useConfirmare()
  const poateScrie = useAuthStore((s) => s.permissions.includes('operational.flota_pv:write'))
  const eAdmin = useAuthStore((s) => s.user?.role === 'admin')
  const masini = useMasini()
  const oameni = useOameni()

  const pv = useQuery({
    queryKey: ['flota', 'pv', 'detaliu', id],
    enabled: !nou,
    queryFn: async () => (await api.get<PvDetaliu>(`/flota/pv/${id}`)).data,
  })
  const istoric = useQuery({
    queryKey: ['flota', 'pv', 'istoric', id],
    enabled: !nou,
    queryFn: async () =>
      (
        await api.get<{ rows: { action: string; at: string; actor: string }[] }>(
          `/flota/pv/${id}/istoric`,
        )
      ).data.rows,
  })

  const [f, setF] = useState<Formular>(() => ({
    vehicleId: sp.get('masina') ?? '',
    predatorId: sp.get('predator') ?? FIRMA,
    primitorId: FIRMA,
    predatLa: sp.get('la') ? laInput(sp.get('la')!) : laInput(new Date().toISOString()),
    kilometraj: '',
    nivelCarburant: '50',
    stare: '',
    daune: '',
    actTalon: true,
    actRca: true,
    actItp: true,
    actRovinieta: true,
    cardCarburant: true,
    nrChei: '2',
    alteAccesorii: '',
    observatii: '',
  }))
  const incarcat = useRef<number | null>(null)
  useEffect(() => {
    if (pv.data && incarcat.current !== pv.data.id) {
      incarcat.current = pv.data.id
      setF(dinPv(pv.data))
    }
  }, [pv.data])

  const reincarca = () => {
    void qc.invalidateQueries({ queryKey: ['flota'] })
    void qc.invalidateQueries({ queryKey: ['rompetrol'] })
  }

  const salveaza = useMutation({
    mutationFn: async () => {
      if (
        !nou &&
        pv.data?.numar &&
        !(await confirma({
          titlu: 'Ciorna a fost deja tipărită',
          mesaj: `Exemplarul tipărit ${pv.data.numar} nu va mai corespunde cu ce scrie în CRM. După salvare retipărește-l și semnați-l pe cel nou; pe cel vechi aruncă-l.`,
          buton: 'Salvează și retipăresc',
        }))
      )
        throw new Error('anulat')
      const body = catreApi(f)
      return nou
        ? (await api.post<{ id: number }>('/flota/pv', body)).data
        : (await api.patch<{ id: number }>(`/flota/pv/${id}`, body)).data
    },
    onSuccess: (r) => {
      toast.success('Ciorna e salvată.')
      reincarca()
      if (nou) nav(`/operational/flota/pv/${r.id}`, { replace: true })
    },
    onError: (e) => {
      if ((e as Error)?.message !== 'anulat') toast.error(mesajEroare(e))
    },
  })

  const semneaza = useMutation({
    mutationFn: async (motiv: string | null) =>
      (await api.post<{ numar: string }>(`/flota/pv/${id}/semneaza`, { motiv })).data,
    onSuccess: (r) => {
      toast.success(`PV-ul ${r.numar} e semnat. Alimentările din perioada lui s-au atribuit.`)
      reincarca()
    },
    onError: (e) => toast.error(mesajEroare(e)),
  })

  const p = pv.data
  const editabil = poateScrie && (nou || p?.status === 'ciorna')
  const set = <K extends keyof Formular>(k: K, v: Formular[K]) => setF((x) => ({ ...x, [k]: v }))

  if (!nou && pv.isLoading) return <div className="p-4 text-slate-500">Se încarcă…</div>
  if (!nou && pv.isError) return <div className="p-4 text-rose-600">{mesajEroare(pv.error)}</div>

  const valid = f.vehicleId && f.predatLa && f.kilometraj !== '' && f.nivelCarburant !== ''
  const modificat = !nou && p && JSON.stringify(dinPv(p)) !== JSON.stringify(f)

  async function pdf() {
    try {
      await deschideFisier(`/flota/pv/${id}/pdf`)
      reincarca()
    } catch (e) {
      toast.error(mesajEroare(e))
    }
  }

  async function sterge() {
    if (!(await confirma({ titlu: 'Ștergi ciorna?', buton: 'Șterge ciorna', pericol: true })))
      return
    try {
      await api.delete(`/flota/pv/${id}`)
      reincarca()
      nav('/operational/flota/procese-verbale')
    } catch (e) {
      toast.error(mesajEroare(e))
    }
  }

  async function anuleaza() {
    const motiv = await cere({
      titlu:
        p?.status === 'semnat' ? 'Anulezi procesul-verbal semnat?' : 'Anulezi ciorna tipărită?',
      mesaj:
        'Rămâne în serie, marcat anulat. Perioada lui iese din istorie, iar alimentările din ea pot rămâne nealocate.',
      eticheta: 'Motivul anulării',
      buton: 'Anulează PV-ul',
      obligatoriu: true,
    })
    if (!motiv) return
    try {
      await api.post(`/flota/pv/${id}/anuleaza`, { motiv })
      toast.success('PV-ul e anulat.')
      reincarca()
    } catch (e) {
      toast.error(mesajEroare(e))
    }
  }

  async function incarca(fel: 'poze' | 'scan', fisier: File) {
    try {
      const fd = new FormData()
      const deUrcat = fel === 'poze' ? (await micsoreazaPoza(fisier)).fisier : fisier
      fd.append('file', deUrcat)
      await api.post(`/flota/pv/${id}/${fel}`, fd, { timeout: 120_000 })
      toast.success(fel === 'poze' ? 'Poza e adăugată.' : 'Scanul e încărcat.')
      reincarca()
    } catch (e) {
      toast.error(mesajEroare(e))
    }
  }

  return (
    <div className="space-y-4">
      <AntetFix>
        <div className="flex flex-wrap items-center gap-2">
          <PenLine className="h-6 w-6 text-orange-500" />
          <h1 className="text-2xl font-bold">
            {nou ? 'Proces-verbal nou' : `Proces-verbal ${p?.numar ?? `(ciornă #${p?.id})`}`}
          </h1>
          {p && <StarePv status={p.status} />}
          {p?.retroactiv && <Pastila ton="albastru">introdus retroactiv</Pastila>}
          <Link
            to="/operational/flota/procese-verbale"
            className="ml-2 text-sm text-slate-500 hover:text-slate-700"
          >
            ← Toate PV-urile
          </Link>
        </div>
        {p && (
          <p className="text-xs text-slate-500">
            Introdus de {p.numeCreator ?? '—'} la {cand(p.createdAt)}
            {p.semnatLa && ` · semnat în CRM de ${p.numeSemnatar ?? '—'} la ${cand(p.semnatLa)}`}
            {p.anulatLa &&
              ` · anulat de ${p.numeAnulator ?? '—'} la ${cand(p.anulatLa)}: „${p.motivAnulare}”`}
          </p>
        )}
      </AntetFix>

      {p?.verificare && (p.verificare.erori.length > 0 || p.verificare.avertizari.length > 0) && (
        <div className="space-y-2">
          {p.verificare.erori.length > 0 && (
            <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
              <div className="mb-1 flex items-center gap-1 font-medium">
                <XCircle className="h-4 w-4" /> Nu se poate semna încă
              </div>
              <ul className="list-disc space-y-1 pl-5">
                {p.verificare.erori.map((e, i) => (
                  <li key={i}>
                    {e.mesaj}{' '}
                    {e.pvId && (
                      <Link to={`/operational/flota/pv/${e.pvId}`} className="underline">
                        vezi PV-ul
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {p.verificare.avertizari.length > 0 && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              <div className="mb-1 flex items-center gap-1 font-medium">
                <AlertTriangle className="h-4 w-4" /> De știut
              </div>
              <ul className="list-disc space-y-1 pl-5">
                {p.verificare.avertizari.map((a, i) => (
                  <li key={i}>{a.mesaj}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <Sectiune titlu="Mașina și părțile">
          <Camp eticheta="Mașina">
            <select
              disabled={!editabil}
              value={f.vehicleId}
              onChange={(e) => set('vehicleId', e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
            >
              <option value="">Alege mașina…</option>
              {(masini.data ?? []).map((m) => (
                <option key={m.id} value={m.id}>
                  {m.plate}
                  {m.brand ? ` — ${m.brand} ${m.model ?? ''}` : ''}
                </option>
              ))}
            </select>
          </Camp>
          <Camp eticheta="Predă">
            <SelectOm
              disabled={!editabil}
              value={f.predatorId}
              onChange={(v) => set('predatorId', v)}
              oameni={oameni.data ?? []}
            />
          </Camp>
          <Camp eticheta="Primește">
            <SelectOm
              disabled={!editabil}
              value={f.primitorId}
              onChange={(v) => set('primitorId', v)}
              oameni={oameni.data ?? []}
            />
          </Camp>
          <Camp eticheta="Data și ora predării">
            <input
              type="datetime-local"
              disabled={!editabil}
              value={f.predatLa}
              onChange={(e) => set('predatLa', e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
            />
          </Camp>
        </Sectiune>

        <Sectiune titlu="Starea la predare">
          <div className="grid grid-cols-2 gap-3">
            <Camp eticheta="Kilometraj (km)">
              <input
                type="number"
                min={0}
                disabled={!editabil}
                value={f.kilometraj}
                onChange={(e) => set('kilometraj', e.target.value)}
                className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
              />
            </Camp>
            <Camp eticheta="Carburant în rezervor (%)">
              <input
                type="number"
                min={0}
                max={100}
                step={5}
                disabled={!editabil}
                value={f.nivelCarburant}
                onChange={(e) => set('nivelCarburant', e.target.value)}
                className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
              />
            </Camp>
          </div>
          <Camp eticheta="Starea generală">
            <textarea
              disabled={!editabil}
              value={f.stare}
              onChange={(e) => set('stare', e.target.value)}
              rows={2}
              placeholder="ex. curată, fără martori aprinși pe bord"
              className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
            />
          </Camp>
          <Camp eticheta="Daune constatate">
            <textarea
              disabled={!editabil}
              value={f.daune}
              onChange={(e) => set('daune', e.target.value)}
              rows={2}
              placeholder="Gol = fără daune. Pune și poze, mai jos."
              className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
            />
          </Camp>
        </Sectiune>

        <Sectiune titlu="Acte și accesorii predate">
          <div className="grid grid-cols-2 gap-2 text-sm">
            {(
              [
                ['actTalon', 'Talon'],
                ['actRca', 'RCA'],
                ['actItp', 'ITP'],
                ['actRovinieta', 'Rovinietă'],
                ['cardCarburant', 'Card(uri) de carburant'],
              ] as const
            ).map(([k, et]) => (
              <label key={k} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  disabled={!editabil}
                  checked={f[k]}
                  onChange={(e) => set(k, e.target.checked)}
                />
                {et}
              </label>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Camp eticheta="Chei">
              <input
                type="number"
                min={0}
                max={10}
                disabled={!editabil}
                value={f.nrChei}
                onChange={(e) => set('nrChei', e.target.value)}
                className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
              />
            </Camp>
          </div>
          <Camp eticheta="Alte accesorii">
            <input
              disabled={!editabil}
              value={f.alteAccesorii}
              onChange={(e) => set('alteAccesorii', e.target.value)}
              placeholder="trusă, extinctor, triunghiuri, roată de rezervă…"
              className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
            />
          </Camp>
        </Sectiune>

        <Sectiune titlu="Observații">
          <textarea
            disabled={!editabil}
            value={f.observatii}
            onChange={(e) => set('observatii', e.target.value)}
            rows={5}
            className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
          />
        </Sectiune>
      </div>

      {/* Actiunile */}
      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white p-3">
        {editabil && (
          <button
            disabled={!valid || salveaza.isPending || (!nou && !modificat)}
            onClick={() => salveaza.mutate()}
            className="rounded-lg bg-slate-800 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-40"
          >
            {salveaza.isPending
              ? 'Se salvează…'
              : nou
                ? 'Salvează ciorna'
                : 'Salvează modificările'}
          </button>
        )}
        {!nou && p && (
          <button
            onClick={() => void pdf()}
            className="flex items-center gap-1 rounded-lg border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50"
          >
            <FileText className="h-4 w-4" />{' '}
            {p.status === 'ciorna' ? 'Tipărește pentru semnat' : 'PDF'}
          </button>
        )}
        {!nou && p?.status === 'ciorna' && poateScrie && (
          <button
            disabled={
              !!modificat ||
              !p.areScan ||
              semneaza.isPending ||
              (p.verificare?.erori.length ?? 0) > 0
            }
            title={
              modificat
                ? 'Salvează întâi modificările'
                : !p.areScan
                  ? 'Încarcă întâi scanul semnat de ambele părți'
                  : undefined
            }
            onClick={async () => {
              // Acelasi om cu doua masini deodata: se poate, dar cu motiv scris (ramane in audit).
              const deMotivat = (p.verificare?.avertizari ?? []).filter(
                (a) => a.cod === 'sofer_are_alta_masina',
              )
              let motiv: string | null = null
              if (deMotivat.length) {
                motiv = await cere({
                  titlu: 'Același om are și altă mașină în perioada asta',
                  mesaj: deMotivat.map((a) => a.mesaj).join(' '),
                  eticheta: 'De ce le are pe amândouă (rămâne în istoricul PV-ului)',
                  buton: 'Semnează cu motivul ăsta',
                  obligatoriu: true,
                })
                if (!motiv) return
              } else if (
                !(await confirma({
                  titlu: 'Marchezi PV-ul ca semnat?',
                  mesaj:
                    'Fă asta după ce ambele părți au semnat hârtia și ai încărcat scanul. Un PV semnat nu se mai modifică; din el se atribuie alimentările.',
                  buton: 'Semnat',
                }))
              )
                return
              semneaza.mutate(motiv)
            }}
            className="rounded-lg bg-orange-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-orange-600 disabled:opacity-40"
          >
            Marchează semnat
          </button>
        )}
        {!nou && p?.status === 'ciorna' && poateScrie && !p.numar && (
          <button
            onClick={() => void sterge()}
            className="ml-auto flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm text-rose-600 hover:bg-rose-50"
          >
            <Trash2 className="h-4 w-4" /> Șterge ciorna
          </button>
        )}
        {!nou && (p?.status === 'semnat' || (p?.status === 'ciorna' && p.numar)) && eAdmin && (
          <button
            onClick={() => void anuleaza()}
            className="ml-auto rounded-lg px-3 py-1.5 text-sm text-rose-600 hover:bg-rose-50"
          >
            Anulează PV-ul
          </button>
        )}
      </div>

      {!nou && p && (
        <div className="grid gap-4 lg:grid-cols-2">
          <Sectiune titlu={`Poze (${p.poze.length})`}>
            <div className="flex flex-wrap gap-2">
              {p.poze.map((z) => (
                <PozaMica key={z.id} pvId={p.id} poza={z} stergibila={editabil} />
              ))}
              {p.poze.length === 0 && (
                <span className="text-sm text-slate-500">
                  Nicio poză. Daunele se dovedesc cu poze.
                </span>
              )}
            </div>
            {editabil && (
              <AlegeFisier
                accept="image/*"
                eticheta="Adaugă poză"
                icon={<Camera className="h-4 w-4" />}
                onAles={(x) => void incarca('poze', x)}
              />
            )}
          </Sectiune>

          <Sectiune titlu="Scanul semnat">
            {p.areScan ? (
              <button
                onClick={() =>
                  void deschideFisier(`/flota/pv/${p.id}/scan`).catch((e) =>
                    toast.error(mesajEroare(e)),
                  )
                }
                className="text-sm text-orange-700 hover:underline"
              >
                {p.scanNume} (încărcat {cand(p.scanIncarcatLa)})
              </button>
            ) : (
              <p className="text-sm text-slate-500">
                Încă nu e încărcat. Tipărește PV-ul, semnați-l amândoi, apoi încarcă poza sau PDF-ul
                — fără scan, PV-ul nu se poate marca semnat.
              </p>
            )}
            {poateScrie && p.status !== 'anulat' && (
              <AlegeFisier
                accept="application/pdf,image/jpeg,image/png"
                eticheta={p.areScan ? 'Înlocuiește scanul' : 'Încarcă scanul'}
                icon={<Upload className="h-4 w-4" />}
                onAles={(x) => void incarca('scan', x)}
              />
            )}
          </Sectiune>

          <Sectiune titlu="Istoric">
            <ul className="space-y-1 text-sm text-slate-600">
              {(istoric.data ?? []).map((r, i) => (
                <li key={i}>
                  {cand(r.at)} — {r.actor}:{' '}
                  {r.action === 'create'
                    ? 'a introdus PV-ul'
                    : r.action === 'status_transition'
                      ? 'a schimbat starea'
                      : r.action === 'delete'
                        ? 'a șters ciorna'
                        : 'a modificat'}
                </li>
              ))}
            </ul>
            <p className="text-xs text-slate-400">
              Predă: <LinkSofer id={p.predatorId} nume={p.numePredator} /> · Primește:{' '}
              <LinkSofer id={p.primitorId} nume={p.numePrimitor} /> · Mașina:{' '}
              <Link to={`/operational/flota/masini/${p.vehicleId}`} className="hover:underline">
                {p.vehicle.plate}
              </Link>
            </p>
          </Sectiune>
        </div>
      )}
    </div>
  )
}

function Sectiune({ titlu, children }: { titlu: string; children: React.ReactNode }) {
  return (
    <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-4">
      <div className="text-sm font-medium text-slate-700">{titlu}</div>
      {children}
    </div>
  )
}

function Camp({ eticheta, children }: { eticheta: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1 block text-[11px] uppercase text-slate-400">{eticheta}</label>
      {children}
    </div>
  )
}

function SelectOm({
  value,
  onChange,
  oameni,
  disabled,
}: {
  value: string
  onChange: (v: string) => void
  oameni: { id: number; name: string; jobTitle: string | null }[]
  disabled?: boolean
}) {
  return (
    <select
      disabled={disabled}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
    >
      <option value={FIRMA}>Firma (mașina la sediu, fără șofer)</option>
      {oameni.map((o) => (
        <option key={o.id} value={o.id}>
          {o.name}
          {o.jobTitle ? ` — ${o.jobTitle}` : ''}
        </option>
      ))}
    </select>
  )
}

function AlegeFisier({
  accept,
  eticheta,
  icon,
  onAles,
}: {
  accept: string
  eticheta: string
  icon: React.ReactNode
  onAles: (f: File) => void
}) {
  const ref = useRef<HTMLInputElement>(null)
  return (
    <>
      <input
        ref={ref}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => {
          const x = e.target.files?.[0]
          if (x) onAles(x)
          e.target.value = ''
        }}
      />
      <button
        onClick={() => ref.current?.click()}
        className="flex items-center gap-1 rounded-lg border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50"
      >
        {icon} {eticheta}
      </button>
    </>
  )
}

/** Miniatura unei poze. Se aduce cu autentificarea pusa: un `<img src>` simplu ar primi 401. */
function PozaMica({
  pvId,
  poza,
  stergibila,
}: {
  pvId: number
  poza: { id: number; numeFisier: string }
  stergibila: boolean
}) {
  const qc = useQueryClient()
  const url = `/flota/pv/${pvId}/poze/${poza.id}`
  const src = useQuery({
    queryKey: ['flota', 'poza', pvId, poza.id],
    queryFn: async () =>
      URL.createObjectURL((await api.get(url, { responseType: 'blob' })).data as Blob),
    staleTime: Infinity,
  })
  return (
    <div className="relative">
      <button onClick={() => void deschideFisier(url)} title={poza.numeFisier}>
        {src.data ? (
          <img src={src.data} alt={poza.numeFisier} className="h-24 w-24 rounded-lg object-cover" />
        ) : (
          <div className="h-24 w-24 rounded-lg bg-slate-100" />
        )}
      </button>
      {stergibila && (
        <button
          onClick={async () => {
            try {
              await api.delete(url)
              void qc.invalidateQueries({ queryKey: ['flota', 'pv', 'detaliu'] })
            } catch (e) {
              toast.error(mesajEroare(e))
            }
          }}
          className="absolute right-1 top-1 rounded bg-white/90 p-0.5 text-rose-600"
          title="Șterge poza"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  )
}
